import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  filter,
  first,
  map,
  startWith,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { getEnumAsArray } from 'src/app/shared/utils'
import { Competition } from '../../competition'
import { CompetitionMatch } from '../../competition-match/competition-match'
import { competitionMatchActions } from '../../competition-match/competition-match.actions'
import { competitionMatchSelectors } from '../../competition-match/competition-match.selectors'
import { CompetitionTeam } from '../../competition-team/competition-team'
import { competitionTeamSelectors } from '../../competition-team/competition-team.selectors'
import { CompetitionWhitelist } from '../../competition-whitelist/competition-whitelist'
import { competitionWhitelistSelectors } from '../../competition-whitelist/competition-whitelist.selectors'
import { competitionActions } from '../../competition.actions'
import { competitionSelectors } from '../../competition.selectors'
import {
  BlacklistWhitelistDays,
  CompetitionBlacklist
} from '../competition-blacklist'
import { competitionBlacklistActions } from '../competition-blacklist.actions'
import { competitionBlacklistSelectors } from '../competition-blacklist.selectors'
import { ActivatedRoute } from '@angular/router'

@UntilDestroy()
@Component({
  selector: 'blacklist-card',
  templateUrl: './blacklist-card.component.html',
  styleUrls: ['./blacklist-card.component.scss']
})
export class BlackListCardComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<any>()
  public form: UntypedFormGroup
  public days = getEnumAsArray(BlacklistWhitelistDays)
  public dayOptions$: Observable<any[]>
  public teamId$: Observable<number>
  public team$: Observable<CompetitionTeam>
  public isDisabled: boolean = false
  public manualDateInput: boolean = false
  public competitionEntities$: Observable<Dictionary<Competition>>
  public selectedDayIds: any[]
  public selectedBlackListDays$: Observable<CompetitionBlacklist[]>
  public selectedWhiteListDays$: Observable<CompetitionWhitelist[]>
  public selectedLanguage$: Observable<string>
  public competitionMatches$: Observable<CompetitionMatch[]>
  private teamId: number
  private competitionId: number
  private blackMaxList: number = 0
  private startDate: string
  private endDate: string
  private selectedBlackListDays: any[]
  private blackListData$ = new Subject<any>()
  private selectBlackList$: Observable<any>

  get daysFormArray() {
    return this.form.controls.days as UntypedFormArray
  }

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private actions$: Actions,
    private notificationService: NotificationService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.selectBlackList$ = this.store.select(
      competitionBlacklistSelectors.selectCurrentBlackListObjects
    )
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    this.competitionEntities$ = this.store.select(
      competitionSelectors.selectEntities
    )

    this.form = this.fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      days: new UntypedFormArray([]),
      dateOption: ['wholeCompetition', Validators.required]
    })

    this.days.forEach(() => this.daysFormArray.push(new UntypedFormControl(false)))

    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )
    this.team$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      ),
      tap((team) => {
        if (team && this.competitionId != team.competitionId) {
          this.competitionId = team.competitionId
          this.store.dispatch(
            competitionActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { id: team?.competitionId }
              })
            })
          )
          this.store.dispatch(
            competitionMatchActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { competitionId: team?.competitionId },
                join: [{ field: 'booking' }]
              })
            })
          )
          this.store.dispatch(
            competitionBlacklistActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { competitionId: team?.competitionId }
              })
            })
          )
        }
      })
    )

    this.competitionMatches$ = this.team$.pipe(
      switchMap((team) => {
        return this.store.select(competitionMatchSelectors.selectByFilter, {
          field: 'competitionId',
          value: team?.competitionId
        })
      })
    )

    this.selectedWhiteListDays$ = this.team$.pipe(
      switchMap((team) =>
        this.store.select(competitionWhitelistSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: team?.id
        })
      )
    )

    let data = []
    this.selectBlackList$
      .pipe(take(1))
      .subscribe((blackList) => (data = blackList))

    this.selectedBlackListDays$ = this.team$.pipe(
      switchMap((team) =>
        this.store.select(competitionBlacklistSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: team?.id
        })
      ),
      map((selectedBlackListDays) => {
        if (data?.length == 0) {
          if (selectedBlackListDays.length !== 0) {
            this.blackListData$.next(selectedBlackListDays)
          }
        }
        this.selectedBlackListDays = selectedBlackListDays.sort(
          (a, b) => a.weekday - b.weekday
        )
        return selectedBlackListDays.sort((a, b) => a.weekday - b.weekday)
      })
    )

    this.dayOptions$ = combineLatest([
      this.store.select(bookingSelectors.selectEntities),
      this.selectedBlackListDays$,
      this.store.select(
        competitionWhitelistSelectors.selectCurrentWhiteListObjects
      )
    ]).pipe(
      untilDestroyed(this),
      map(
        ([bookingEntitites, selectedBlackListDays, selectedWhiteListDays]) => {
          let startTimesOfBookings = []

          for (let i = 0; i < 8; i++) {
            startTimesOfBookings[i] = []
          }

          let linkedProductId: number
          this.store
            .select(competitionSelectors.selectEntities)
            .pipe(take(1))
            .subscribe((competition) => {
              linkedProductId =
                competition[this.competitionId]?.linkedChildProductId
            })
          Object.values(bookingEntitites)
            .filter((b) => b.linkedProductId === linkedProductId)
            .forEach((booking) => {
              const date = moment(booking?.startDate)
              const dow = date.day() === 0 ? 7 : date.day()

              if (
                booking &&
                !selectedWhiteListDays?.find(
                  (whiteListDay) => whiteListDay.weekday == dow
                ) &&
                !selectedBlackListDays?.find(
                  (blackListDay) => blackListDay.weekday == dow
                )
              ) {
                startTimesOfBookings[dow].push(
                  moment(booking?.startDate).format('HH:mm')
                )
                startTimesOfBookings[dow].push(
                  moment(booking?.endDate).format('HH:mm')
                )
              }
            })

          Object.keys(startTimesOfBookings).forEach((day) => {
            startTimesOfBookings[day].sort(
              (a, b) => a.replace(':', '') - b.replace(':', '')
            )
          })
          return startTimesOfBookings
        }
      )
    )

    this.store.dispatch(
      competitionBlacklistActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { competitionTeamId: this.teamId }
        })
      })
    )

    this.blackListData$.pipe(take(1)).subscribe((blackList) => {
      this.store.dispatch(
        competitionBlacklistActions.setCurrentBlackList({
          currentBlackListSelected: blackList
        })
      )
      if (blackList.length === 0) {
        this.store.dispatch(
          competitionBlacklistActions.deleteAllCurrentBlackList()
        )
      }
    })

    combineLatest([
      this.form.controls.dateOption.valueChanges.pipe(
        startWith('wholeCompetition')
      ),
      this.team$,
      this.store.select(competitionSelectors.selectEntities)
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([startDate, team, competitionEntities]) => {
        if (startDate == 'wholeCompetition') {
          this.form.controls.startDate.patchValue(
            competitionEntities[team?.competitionId]?.startDate
          )
          this.form.controls.endDate.patchValue(
            competitionEntities[team?.competitionId]?.endDate
          )
        } else {
          this.form.controls.startDate.patchValue(null)
          this.form.controls.endDate.patchValue(null)
        }
      })

    this.form.controls.days.valueChanges
      .pipe(untilDestroyed(this), startWith([]))
      .subscribe((days) => {
        this.selectedDayIds = []

        days.forEach((checked, i) => {
          checked
            ? i == 0
              ? this.selectedDayIds.push(null)
              : this.selectedDayIds.push(i)
            : null
        })
      })
  }

  public onInputChange(value: string) {
    if (value == 'dateRange') {
      this.manualDateInput = true
    } else {
      this.manualDateInput = false
    }
  }

  public onClickRemove(blacklistDay) {
    this.store.dispatch(
      competitionBlacklistActions.deleteOne({ id: blacklistDay.id })
    )

    this.store.dispatch(
      competitionBlacklistActions.deleteCurrentBlackList({
        currentBlackListSelected: blacklistDay
      })
    )

    this.actions$
      .pipe(
        ofType(competitionBlacklistActions.removeOne),
        first(),
        timeout(5000),
        catchError((error) => {
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.timeoutError', 'warning')
          return
        }
        this.notificationService.emit('alerts.success', 'success')
      })
  }
  public dateChangeBlackList(event: any, typeDate: string): void {
    if (event !== null) {
      if (typeDate === 'startDate') {
        this.startDate = moment(event._d).format('YYYY-MM-DD hh:mm:ss')
      } else {
        this.endDate = moment(event._d).format('YYYY-MM-DD hh:mm:ss')
      }
    }
  }
  public checkDates(): any {
    if (
      this.manualDateInput &&
      moment(this.endDate).diff(moment(this.startDate), 'days') <= 7 &&
      moment(this.endDate).diff(moment(this.startDate), 'days') > 0
    ) {
      return false
    } else {
      return true
    }
  }
  public getMaxBlackList(blackMaxList) {
    this.blackMaxList = blackMaxList
    return blackMaxList
  }
  public onFormSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      this.notificationService.emit('alerts.emptyCampsForm', 'error')
      return
    }
    const f = this.form.value
    this.team$.pipe(take(1)).subscribe((team) => {
      const selectedDays = f.days.filter((item) => item === true)

      if (
        this.selectedBlackListDays.length + selectedDays.length <=
        this.blackMaxList
      ) {
        if (this.checkDates() && this.manualDateInput) {
          Object.keys(this.form.controls).forEach((key) => {
            this.form.controls[key].markAsDirty()
          })
          this.notificationService.emit('alerts.invalidDateRange', 'warning')
        } else {
          if (this.manualDateInput) {
            this.onSubmit.emit({
              ...f,
              days: [moment(f.startDate).isoWeekday()],

              startDate: moment(f.startDate),

              endDate: moment(f.endDate),

              teamId: team?.id
            })
          } else {
            this.onSubmit.emit({
              ...f,
              days: this.selectedDayIds,

              teamId: team?.id
            })

            this.daysFormArray.controls.forEach((day) => {
              day.setValue(false)
            })
          }
        }
      } else {
        this.notificationService.emit(
          'alerts.maximumOptionsSelected',
          'warning'
        )
      }
    })
  }

  public changeSelectedBlackListOption(dayIndex, event) {
    this.team$.pipe(take(1)).subscribe((team) => {
      const blackListSelected = {
        weekday: dayIndex,
        competitionId: team?.competitionId,
        competitionTeamId: team?.id,
        startDate: this.form.value.startDate,
        endDate: this.form.value.endDate,
        type: 'blacklist'
      }

      if (event.target.checked) {
        this.store.dispatch(
          competitionBlacklistActions.setCurrentBlackList({
            currentBlackListSelected: [blackListSelected]
          })
        )
      } else {
        this.store.dispatch(
          competitionBlacklistActions.deleteCurrentBlackList({
            currentBlackListSelected: blackListSelected
          })
        )
      }
    })
  }
}
